import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { MatchResult } from 'Src/models/event';

const individualScoreBatchQueues = new Map<
  string,
  {
    user: AppUser;
    eventId: string;
    scores: any[];
    resolve: Array<(value: any) => void>;
    reject: Array<(error: any) => void>;
  }
>();
const teamScoreBatchQueues = new Map<
  string,
  {
    user: AppUser;
    eventId: string;
    scores: any[];
    resolve: Array<(value: any) => void>;
    reject: Array<(error: any) => void>;
  }
>();
const houseScoreBatchQueues = new Map<
  string,
  {
    user: AppUser;
    eventId: string;
    scores: any[];
    resolve: Array<(value: any) => void>;
    reject: Array<(error: any) => void>;
  }
>();
const schoolScoreBatchQueues = new Map<
  string,
  {
    user: AppUser;
    eventId: string;
    scores: any[];
    resolve: Array<(value: any) => void>;
    reject: Array<(error: any) => void>;
  }
>();

function queueIndividualScore(user: AppUser, eventId: string, score: any): Promise<any> {
  const key = `${user.activeSchoolId}:${eventId}`;
  let queue = individualScoreBatchQueues.get(key);
  if (!queue) {
    queue = { user, eventId, scores: [], resolve: [], reject: [] };
    individualScoreBatchQueues.set(key, queue);
    setTimeout(() => {
      const pending = individualScoreBatchQueues.get(key);
      if (!pending) return;
      individualScoreBatchQueues.delete(key);
      upsertIndividualScoresBatch(pending.user, pending.eventId, pending.scores)
        .then(result => pending.resolve.forEach(resolve => resolve(result)))
        .catch(error => pending.reject.forEach(reject => reject(error)));
    }, 0);
  }
  return new Promise((resolve, reject) => {
    queue.scores.push(score);
    queue.resolve.push(resolve);
    queue.reject.push(reject);
  });
}

function queueTeamScore(user: AppUser, eventId: string, score: any): Promise<any> {
  const key = `${user.activeSchoolId}:${eventId}`;
  let queue = teamScoreBatchQueues.get(key);
  if (!queue) {
    queue = { user, eventId, scores: [], resolve: [], reject: [] };
    teamScoreBatchQueues.set(key, queue);
    setTimeout(() => {
      const pending = teamScoreBatchQueues.get(key);
      if (!pending) return;
      teamScoreBatchQueues.delete(key);
      upsertTeamScoresBatch(pending.user, pending.eventId, pending.scores)
        .then(result => pending.resolve.forEach(resolve => resolve(result)))
        .catch(error => pending.reject.forEach(reject => reject(error)));
    }, 0);
  }
  return new Promise((resolve, reject) => {
    queue.scores.push(score);
    queue.resolve.push(resolve);
    queue.reject.push(reject);
  });
}

function queueHouseScore(user: AppUser, eventId: string, score: any): Promise<any> {
  const key = `${user.activeSchoolId}:${eventId}`;
  let queue = houseScoreBatchQueues.get(key);
  if (!queue) {
    queue = { user, eventId, scores: [], resolve: [], reject: [] };
    houseScoreBatchQueues.set(key, queue);
    setTimeout(() => {
      const pending = houseScoreBatchQueues.get(key);
      if (!pending) return;
      houseScoreBatchQueues.delete(key);
      upsertHouseScoresBatch(pending.user, pending.eventId, pending.scores)
        .then(result => pending.resolve.forEach(resolve => resolve(result)))
        .catch(error => pending.reject.forEach(reject => reject(error)));
    }, 0);
  }
  return new Promise((resolve, reject) => {
    queue.scores.push(score);
    queue.resolve.push(resolve);
    queue.reject.push(reject);
  });
}

function queueSchoolScore(user: AppUser, eventId: string, score: any): Promise<any> {
  const key = `${user.activeSchoolId}:${eventId}`;
  let queue = schoolScoreBatchQueues.get(key);
  if (!queue) {
    queue = { user, eventId, scores: [], resolve: [], reject: [] };
    schoolScoreBatchQueues.set(key, queue);
    setTimeout(() => {
      const pending = schoolScoreBatchQueues.get(key);
      if (!pending) return;
      schoolScoreBatchQueues.delete(key);
      upsertSchoolScoresBatch(pending.user, pending.eventId, pending.scores)
        .then(result => pending.resolve.forEach(resolve => resolve(result)))
        .catch(error => pending.reject.forEach(reject => reject(error)));
    }, 0);
  }
  return new Promise((resolve, reject) => {
    queue.scores.push(score);
    queue.resolve.push(resolve);
    queue.reject.push(reject);
  });
}

export function createIndividualScore(user: AppUser, eventId: string, data) {
  return queueIndividualScore(user, eventId, data);
}

export function updateIndividualScore(user: AppUser, eventId: string, scoreId: string, data) {
  return queueIndividualScore(user, eventId, { ...data, scoreId });
}

export function upsertIndividualScoresBatch(user: AppUser, eventId: string, scores: any[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/individuals/score/batch`,
      { scores },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function createTeamScore(user: AppUser, eventId: string, data) {
  return queueTeamScore(user, eventId, data);
}

export function updateTeamScore(user: AppUser, eventId: string, scoreId: string, data) {
  return queueTeamScore(user, eventId, { ...data, scoreId });
}

export function upsertTeamScoresBatch(user: AppUser, eventId: string, scores: any[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/teams/score/batch`,
      { scores },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function saveMatchResult(user: AppUser, eventId: string, matchResult: MatchResult, publish = false) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.put(
    `${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/match`,
    { matchResult, publish },
    { headers: { usid: key } }
  ).then(response => response.data);
}

export function publishMatchResult(user: AppUser, eventId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.post(
    `${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/match/publish`,
    {},
    { headers: { usid: key } }
  ).then(response => response.data);
}

export function createHouseScore(user: AppUser, eventId: string, data) {
  return queueHouseScore(user, eventId, data);
}

export function updateHouseScore(user: AppUser, eventId: string, scoreId: string, data) {
  return queueHouseScore(user, eventId, { ...data, scoreId });
}

export function upsertHouseScoresBatch(user: AppUser, eventId: string, scores: any[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/houses/score/batch`,
      { scores },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function createSchoolScore(user: AppUser, eventId: string, data) {
  return queueSchoolScore(user, eventId, data);
}

export function updateSchoolScore(user: AppUser, eventId: string, scoreId: string, data) {
  return queueSchoolScore(user, eventId, { ...data, scoreId });
}

export function upsertSchoolScoresBatch(user: AppUser, eventId: string, scores: any[]) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/schools/score/batch`,
      { scores },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function createCricketResults(user: AppUser, eventId: string, data) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}/results/cricket`, data, config)
    .then(response => {
      return response.data;
    });
}
